import { Check, X, ChevronDown } from 'lucide-react';
import { AmountDisplay } from '@/components/common/AmountDisplay';
import { resolveWalletCategoryForTransaction } from '@/lib/walletCategoryMap';
import type { WalletTransaction } from '@/api/wallet';
import type { Category } from '@sharecost/shared';
import { useTranslation } from 'react-i18next';

interface WalletTransactionCardProps {
  transaction: WalletTransaction;
  isSelected: boolean;
  categories: Category[];
  categoryOverride?: string | null;
  onToggleSelect: (id: string) => void;
  onApprove: (id: string) => void;
  onDismiss: (id: string) => void;
  onCategoryChange?: (id: string, categoryId: string | null) => void;
}

function resolveCategory(
  transaction: WalletTransaction,
  categories: Category[],
): Category | undefined {
  const key = resolveWalletCategoryForTransaction(transaction);
  if (!key) return undefined;
  return categories.find(c => c.key === key);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function Pill({ children, className = '', style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${className}`} style={style}>
      {children}
    </span>
  );
}

export function WalletTransactionCard({
  transaction,
  isSelected,
  categories,
  categoryOverride,
  onToggleSelect,
  onApprove,
  onDismiss,
  onCategoryChange,
}: WalletTransactionCardProps) {
  const { t } = useTranslation();
  const isPending = transaction.status === 'pending';
  const resolvedCategory = resolveCategory(transaction, categories);
  const isUncleared = transaction.raw_data?.recordState === 'uncleared';

  // The effective category: override > resolved from map
  const effectiveCategoryId = categoryOverride !== undefined
    ? categoryOverride
    : resolvedCategory?.id ?? null;
  const effectiveCategory = effectiveCategoryId
    ? categories.find(c => c.id === effectiveCategoryId)
    : resolvedCategory;

  // Only show wallet category when it differs from description (avoids duplication)
  const showWalletCategory = transaction.wallet_category_name
    && transaction.wallet_category_name !== transaction.description
    && transaction.wallet_category_name !== 'Unknown';

  return (
    <div
      className={`rounded-xl border transition-all ${
        isSelected
          ? 'border-brand-300 dark:border-brand-700 bg-brand-50 dark:bg-brand-900/30 shadow-sm'
          : 'border-border-primary bg-surface-primary hover:border-border-secondary hover:shadow-sm'
      }`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          {isPending && (
            <label className="mt-1 flex cursor-pointer">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleSelect(transaction.id)}
                className="h-4 w-4 rounded border-border-secondary text-brand-600 focus:ring-brand-500"
              />
            </label>
          )}

          <div className="flex-1 min-w-0">
            {/* Row 1: Title + Amount */}
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-sm font-semibold text-text-primary truncate">
                {transaction.description}
              </h3>
              <AmountDisplay
                amountMinor={transaction.amount_minor_src}
                currency={transaction.currency_code_src}
                className="text-[15px] font-bold text-text-primary tabular-nums shrink-0"
              />
            </div>

            {/* Row 2: Note — only if it differs from description (avoids duplication) */}
            {transaction.note && transaction.note !== transaction.description && (
              <p className="mt-0.5 text-xs text-text-tertiary truncate leading-relaxed">
                {transaction.note}
              </p>
            )}

            {/* Row 3: Metadata pills */}
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <Pill className="bg-surface-tertiary text-text-secondary">
                {formatDate(transaction.transaction_date)}
              </Pill>

              {transaction.mapping?.wallet_account_name && (
                <Pill className="bg-surface-tertiary text-text-secondary">
                  {transaction.mapping.wallet_account_name}
                </Pill>
              )}

              {transaction.group && (
                <Pill className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: transaction.group.color ?? '#6366F1' }}
                  />
                  {transaction.group.name}
                </Pill>
              )}

              {/* Category mapping: Wallet → ShareCost */}
              {(showWalletCategory || effectiveCategory) && (
                <Pill
                  className="font-medium"
                  style={effectiveCategory ? {
                    backgroundColor: `${effectiveCategory.color}15`,
                    color: effectiveCategory.color,
                  } as React.CSSProperties : undefined}
                >
                  {showWalletCategory && effectiveCategory ? (
                    <>
                      <span className="text-text-tertiary">{transaction.wallet_category_name}</span>
                      <span className="text-text-quaternary mx-0.5">→</span>
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: effectiveCategory.color }}
                      />
                      {effectiveCategory.name}
                    </>
                  ) : effectiveCategory ? (
                    <>
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: effectiveCategory.color }}
                      />
                      {effectiveCategory.name}
                    </>
                  ) : (
                    <span className="text-text-secondary">{transaction.wallet_category_name}</span>
                  )}
                </Pill>
              )}

              {isUncleared && (
                <Pill className="bg-amber-50 dark:bg-amber-900/30 text-amber-600 ring-1 ring-inset ring-amber-200">
                  {t('web:walletTransactionCard.uncleared')}
                </Pill>
              )}

              {/* Status badge (non-pending) */}
              {!isPending && (
                <Pill
                  className={
                    transaction.status === 'approved' || transaction.status === 'auto_approved'
                      ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : 'bg-surface-tertiary text-text-tertiary'
                  }
                >
                  {transaction.status === 'auto_approved' ? t('web:walletTransactionCard.autoApproved') : transaction.status}
                </Pill>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action row (pending only) — visually separated */}
      {isPending && onCategoryChange && (
        <div className="flex items-center justify-between border-t border-border-tertiary px-4 py-2.5">
          <div className="relative">
            <select
              value={effectiveCategoryId ?? ''}
              onChange={e => {
                const val = e.target.value;
                onCategoryChange(transaction.id, val || null);
              }}
              className="appearance-none rounded-md border border-border-primary bg-surface-secondary py-1 pl-2.5 pr-7 text-xs text-text-secondary hover:border-border-secondary focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
            >
              <option value="">{t('web:common.noCategory')}</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 text-text-quaternary" />
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => onApprove(transaction.id)}
              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 dark:bg-green-900/30 transition-colors"
              title={t('web:walletTransactionCard.approveCreateExpense')}
            >
              <Check className="h-3.5 w-3.5" />
              {t('web:common.approve')}
            </button>
            <button
              onClick={() => onDismiss(transaction.id)}
              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-text-quaternary hover:bg-surface-hover hover:text-red-500 dark:hover:text-red-400 transition-colors"
              title={t('web:walletTransactionCard.dismissSkipThisTransaction')}
            >
              <X className="h-3.5 w-3.5" />
              {t('expenses:importedTransactions.action.dismiss')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
